import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from './DigitalIDCard.module.css';
import PlatefullAILogo from '../../images/PlatefullAILogoNB.svg';

interface DigitalIDCardProps {
  className?: string;
  maxItems?: number;
  showActions?: boolean;
  mockUser?: {
    email: string;
    id?: string;
    preferences?: {
      dietary?: string[];
      ingredientPreferences?: { likes?: string[]; avoids?: string[] };
      healthConcerns?: string[] | { concerns?: string[] };
    };
  };
}

// Dietary preferences are now an array of strings
type DietaryPreferenceType = 'vegan' | 'vegetarian' | 'pescatarian' | 'gluten_free' | 'dairy_free' | 'nut_free' | 'egg_free' | 'soy_free' | 'shellfish_free' | 'fish_free' | 'sesame_free' | 'low_fodmap' | 'keto' | 'paleo' | 'atkins' | 'corn_free' | 'mustard_free';

// Use the same interface as AuthContext
interface UserIngredientPreference {
  id: number;
  ingredient_id: number;
  preference_type: 'love' | 'like' | 'dislike' | 'avoid' | 'allergy' | 'intolerance';
  ingredient: {
    id: number;
    name: string;
  };
}

interface HealthConcern {
  id: number;
  name: string;
}

interface UserHealthConcern {
  id: string;
  health_concern_id?: string;
  is_active: boolean;
  health_concern: {
    id: string;
    name: string;
    display_name?: string;
    description?: string;
    is_active?: boolean;
  };
  notes?: string;
}

const DigitalIDCard: React.FC<DigitalIDCardProps> = ({ className, maxItems = 3, showActions = true, mockUser }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Allow optional mock user (used for portal landing preview)
  const effectiveUser: any = mockUser ? (
    {
      email: mockUser.email,
      id: mockUser.id,
      preferences: {
        dietary: mockUser.preferences?.dietary || [],
        ingredientPreferences: mockUser.preferences?.ingredientPreferences || {},
        // Accept either array of strings or object with concerns
        healthConcerns: Array.isArray(mockUser.preferences?.healthConcerns)
          ? mockUser.preferences?.healthConcerns
          : (mockUser.preferences?.healthConcerns as any)?.concerns || []
      }
    }
  ) : user;

  // Use effective user object directly - no local state needed
  const dietaryPreferences = effectiveUser?.preferences?.dietary || [];
  const rawIngredientPreferences = effectiveUser?.preferences?.ingredientPreferences || [];
  const userHealthConcerns = effectiveUser?.preferences?.healthConcerns || [];

  // Normalize ingredient preferences: support array of objects (backend shape)
  // and object with likes/avoids arrays (mock/demo shape)
  let ingredientPreferencesArray: UserIngredientPreference[] = [];
  if (Array.isArray(rawIngredientPreferences)) {
    ingredientPreferencesArray = rawIngredientPreferences as UserIngredientPreference[];
  } else if (rawIngredientPreferences && typeof rawIngredientPreferences === 'object') {
    const likes: string[] = Array.isArray((rawIngredientPreferences as any).likes) ? (rawIngredientPreferences as any).likes : [];
    const avoids: string[] = Array.isArray((rawIngredientPreferences as any).avoids) ? (rawIngredientPreferences as any).avoids : [];
    ingredientPreferencesArray = [
      ...likes.map((name: string, idx: number) => ({
        id: idx,
        ingredient_id: idx,
        preference_type: 'like' as const,
        ingredient: { id: idx, name },
      })),
      ...avoids.map((name: string, idx: number) => ({
        id: likes.length + idx,
        ingredient_id: likes.length + idx,
        preference_type: 'avoid' as const,
        ingredient: { id: likes.length + idx, name },
      })),
    ];
  }
  // No loading state needed - data comes from user object

  // No need to load data - we're using user object directly

  // Helper function to convert text to title case
  const toTitleCase = (text: string): string => {
    if (!text) return text;
    
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to convert dietary preferences to display format
  const getDietaryPreferencesList = (): string[] => {
    if (!dietaryPreferences || dietaryPreferences.length === 0) return [];
    
    const dietaryMapping: Record<string, string> = {
      'vegan': 'Vegan',
      'vegetarian': 'Vegetarian',
      'pescatarian': 'Pescatarian',
      'gluten_free': 'Gluten Free',
      'dairy_free': 'Dairy Free',
      'nut_free': 'Nut Free',
      'egg_free': 'Egg Free',
      'soy_free': 'Soy Free',
      'shellfish_free': 'Shellfish Free',
      'fish_free': 'Fish Free',
      'sesame_free': 'Sesame Free',
      'low_fodmap': 'Low FODMAP',
      'keto': 'Keto',
      'paleo': 'Paleo',
      'atkins': 'Atkins',
      'corn_free': 'Corn Free',
      'mustard_free': 'Mustard Free'
    };
    
    return dietaryPreferences
      .map((pref: string) => dietaryMapping[pref])
      .filter((label: string | undefined): label is string => Boolean(label)); // Filter out undefined mappings
  };

  // Helper function to get likes from ingredient preferences
  const getLikesList = (): string[] => {
    const likes = ingredientPreferencesArray
      .filter((pref: UserIngredientPreference) => ['love', 'like'].includes(pref.preference_type))
      .map((pref: UserIngredientPreference) => toTitleCase(pref.ingredient.name || 'Unknown ingredient'))
      .filter((name: string) => name !== 'Unknown ingredient'); // Filter out null/undefined ingredients
    
    return likes;
  };

  // Helper function to get avoids from ingredient preferences
  const getAvoidsList = (): string[] => {
    const avoids = ingredientPreferencesArray
      .filter((pref: UserIngredientPreference) => ['dislike', 'avoid', 'allergy', 'intolerance'].includes(pref.preference_type))
      .map((pref: UserIngredientPreference) => toTitleCase(pref.ingredient.name || 'Unknown ingredient'))
      .filter((name: string) => name !== 'Unknown ingredient'); // Filter out null/undefined ingredients

    return avoids;
  };

  // Helper function to get health concerns list (supports strings or UserHealthConcern objects)
  const getHealthConcernsList = (): string[] => {
    const concerns = (userHealthConcerns as Array<UserHealthConcern | string>) || [];
    const result: string[] = [];
    for (const concern of concerns) {
      if (typeof concern === 'string') {
        result.push(toTitleCase(concern));
      } else if (concern && concern.health_concern) {
        // Show all health concerns regardless of is_active status
        // The is_active flag appears to be for system availability, not user selection
        const name = concern.health_concern.display_name || concern.health_concern.name || '';
        if (name) {
          result.push(toTitleCase(name));
        }
      }
    }
    return result;
  };

  // Helper function to render tags with limit and +n more
  const renderTagsWithLimit = (items: string[], tagClass?: string) => {
    if (items.length === 0) {
      return (
        <span className={`${styles.preferenceTag} ${styles.emptyTag}`}>
          {user ? 'Set your preferences' : 'Sign up to set preferences'}
        </span>
      );
    }

    const visibleItems = items.slice(0, maxItems);
    const remainingCount = items.length - maxItems;
    
    return (
      <>
        {visibleItems.map((item, index) => (
          <span key={index} className={`${styles.preferenceTag} ${tagClass || ''}`}>
            {item}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className={`${styles.preferenceTag} ${styles.moreTag}`}>
            +{remainingCount} more
          </span>
        )}
      </>
    );
  };

  const handleManageProfile = () => {
    if (user) {
      navigate('/settings');
    } else {
      // Navigate to onboarding with register mode
      navigate('/onboard?mode=register');
    }
  };

  const handleShareId = async () => {
    if (!user) return;
    
    const shareUrl = `${window.location.origin}/id/${user.id}`;
    
    // Try to use native share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Digital Dietary ID',
          text: 'Check out my digital dietary ID on PlatefullAI',
          url: shareUrl,
        });
        return;
      } catch (err) {
        // User cancelled or error occurred, fall back to clipboard
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      // You could add a toast notification here
      alert('Share link copied to clipboard!');
    } catch (err) {
      // Fallback: show the URL in a prompt
      prompt('Copy this link to share your ID:', shareUrl);
    }
  };

  return (
    <div className={`${styles.idCard} ${className || ''}`}>
      <div className={styles.idCardHeader}>
        <div className={styles.idCardLogo}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <div className={styles.idCardType}>DIGITAL DIETARY ID</div>
      </div>

      <div className={styles.idCardBody}>
        <div className={styles.userInfo}>
          <div className={styles.userEmail}>
            {effectiveUser ? effectiveUser.email.split('@')[0] : 'Sign up to create your profile'}
          </div>
        </div>

        <div className={styles.preferencesSection}>
          <div className={styles.preferencesRow}>
            <div className={styles.preferencesLabel}>Diet Preferences</div>
            <div className={styles.preferencesTags}>
              {renderTagsWithLimit(getDietaryPreferencesList())}
            </div>
          </div>

          <div className={styles.preferencesRow}>
            <div className={styles.preferencesLabel}>Health Concerns</div>
            <div className={styles.preferencesTags}>
              {renderTagsWithLimit(getHealthConcernsList(), styles.healthConcernTag)}
            </div>
          </div>

          <div className={styles.preferencesRow}>
            <div className={styles.preferencesLabel}>Likes</div>
            <div className={styles.preferencesTags}>
              {renderTagsWithLimit(getLikesList(), styles.likeTag)}
            </div>
          </div>

          <div className={styles.preferencesRow}>
            <div className={styles.preferencesLabel}>Avoids</div>
            <div className={styles.preferencesTags}>
              {renderTagsWithLimit(getAvoidsList(), styles.avoidTag)}
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div className={styles.idCardFooter}>
          <div className={styles.idCardButtons}>
            <button 
              className={styles.manageButton}
              onClick={handleManageProfile}
            >
              {effectiveUser ? 'Edit Info' : 'Sign Up'}
            </button>
            {effectiveUser && (
              <button 
                className={styles.shareButton}
                onClick={handleShareId}
              >
                Share ID
              </button>
            )}
          </div>
          <div className={styles.idCardId}>
            ID: {effectiveUser ? (effectiveUser.id?.slice(0, 8)?.toUpperCase?.() || 'DEMO-USER') : 'GUEST-XXXX'}
          </div>
        </div>
      )}
      
      {!showActions && (
        <div className={styles.idCardFooter}>
          <div className={styles.idCardId}>
            ID: {effectiveUser ? (effectiveUser.id?.slice(0, 8)?.toUpperCase?.() || 'DEMO-USER') : 'GUEST-XXXX'}
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalIDCard;
